import filter from 'lodash/filter';
import head from 'lodash/head';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import findIndex from 'lodash/findIndex';
import assign from 'lodash/assign';
import times from 'lodash/times';

export class TripPlannerService {
	constructor($resource, ApiService, DateService, BreadcrumbService, GoogleLocationService) {
		'ngInject';

		this.$resource = $resource;
		this.apiService = ApiService;
		this.dateService = DateService;
		this.breadcrumbService = BreadcrumbService;
		this.googleLocationService = GoogleLocationService;

		this.coverageAreaValidationResourceUrl = this.apiService.getResourceUrl('coverage-validation');
		this.tripPlannerPrivateProvidersResourceUrl = this.apiService.getResourceUrl('trip-private-providers');
		this.registeredPublicProvidersResourceUrl = this.apiService.getResourceUrl('registered-public-providers');

		this.tripPlanningLandingPageResourceUrl = this.apiService.getResourceUrl('trip-planning');
		this.tripPlanningLandingPageResource = this.$resource(this.tripPlanningLandingPageResourceUrl);

		this.coverageAreaValidationResource = this.$resource(this.coverageAreaValidationResourceUrl);
		this.tripPlannerPrivateProvidersResource = this.$resource(this.tripPlannerPrivateProvidersResourceUrl);
		this.registeredPublicProvidersResource = this.$resource(this.registeredPublicProvidersResourceUrl);

	}

	validateCoverageArea(serviceAreaDetails) {
		const serviceAreas = {
			service_areas: this._setCoverageAreaObject(serviceAreaDetails)
		};
		const promise = this.coverageAreaValidationResource.get(serviceAreas).$promise;

		return promise
			.then(details => details)
			.catch(err => {
				throw this.apiService.getErrorMessage('Selected area is not serviced for trip planning');
			});
	}

	getPrivateProvidersDetails(details) {
		const promise = this.tripPlannerPrivateProvidersResource.get(this._setDirectionDetailsObject(details)).$promise;

		return promise
			.then(details => ({
				serviceTypeList: this._getServiceTypeObject(details.data),
				providers: this._getContentObject(details.data)
			}))
			.catch(err => {
				throw this.apiService.getErrorMessage(err);
			});
	}

	getdirectionDetailsObject({ originName, destinationName, directionLatLngInfo: { origin, destination } }) {
		return {
			type: "privateTrip",
			origin: {
				latlng: this._getLatLngObject(origin),
				details: {
					name: originName,
					description: ''
				}
			},
			destination: {
				latlng: this._getLatLngObject(destination),
				details: {
					name: destinationName,
					description: ''
				}
			}
		};
	}

	getPublicProviderDetailsAndMapWithRoutes(routes) {
		let agenciesName = reduce(routes, ((agenciesName, route) => {
			agenciesName = agenciesName.concat(this._getAgenciesNamesFromRoute(route));
			return agenciesName;
		}), []);

		agenciesName = uniq(agenciesName);
		if (agenciesName.length > 0) {
			let promise = this.registeredPublicProvidersResource.get({
				agencies_names: agenciesName
			}).$promise;

			return promise.then(({ data }) => {
				return _mapAgenciesWithRoutes.call(this, routes, data);
			});
		} else {
			return Promise.reject(false);
		}

		function _mapAgenciesWithRoutes(routes, providerDetails) {
			const filteredRoutes = reduce(routes, (filteredRoutes, route, index) => {
				const agenciesName = uniq(this._getAgenciesNamesFromRoute(route));
				const agenciesDetails = this._filterAgenciesWhichExist(agenciesName, providerDetails);
				if (agenciesDetails.length === agenciesName.length) {
					filteredRoutes.push(this._getRouteDetailsObject(agenciesDetails, route));
				}
				return filteredRoutes;
			}, []);
			return filteredRoutes;
		}
	}

	getPublicRoutesStepsObject(steps) {
		return steps.map(({ travelMode, latlngs }) => ({ mode: travelMode, latlngs: latlngs }));
	}

	getPublicRoutesStopsObject(steps) {
		const stops = filter(steps, (step, index) => ((steps.length - 1) !== index));
		return stops.map(({ endLocation, instructions }) => ({ latlng: endLocation, details: { name: instructions, description: "" } }));
	}

	getTripPlanningLandingPageDetails() {
		const promise = this.tripPlanningLandingPageResource.get().$promise;

		return promise.then(details => ({
			details: this._getTripPlanningLandingPageDetailsObject(details.data)
		})).catch((error) => {
			throw this.apiService.getErrorMessage(error);
		});
	}

	getTripPlanningSortByOptions(sortBy) {
		switch (sortBy) {
			case 'byCost':
				return [{
					id: 1,
					name: "lowest price"
				}, {
					id: 2,
					name: "highest price"
				}
				];
			case 'byTime':
				return [{
					id: 3,
					name: "shortest travel time"
				}, {
					id: 4,
					name: "longest travel time"
				}];

		};
	}

	getSpecificRequirementsOptionsObject() {
		return [{
			name: 'curb_to_curb',
			label: 'curb to curb',
			value: 1
		}, {
			name: 'door_through_door',
			label: 'door through door',
			value: 2
		}, {
			name: 'door_to_door',
			label: 'door to door',
			value: 3
		}, {
			name: 'wheelchair_access',
			label: 'wheelchair access',
			value: 4
		}];
	}

	_setCoverageAreaObject(details) {
		return details.map(({ latlng, placeShortName: name }) => ({ name, latlng }));
	}

	_filterAgenciesWhichExist(agenciesNamesFromRoutes, agenciesDetailsFromDb) {
		return reduce(agenciesNamesFromRoutes, (arr, val) => {
			const index = findIndex(agenciesDetailsFromDb, ({ name }) => name === val);
			if (index !== -1) {
				arr.push(agenciesDetailsFromDb[index]);
			}
			return arr;
		}, []);
	}

	_getTripPlanningLandingPageDetailsObject(values) {
		let details = filter(values, ['name', 'TripPlanning']);
		this.breadcrumbService.setPageBreadcrumbs(head(details).breadcrumb);
		return _getContentData(head(details).values);

		function _getContentData(data) {
			return data.map(({ description }) => ({ description }))
		}
	}

	_getRouteDetailsObject(data, { legs, overview_polyline: encodedPolyline }) {
		return {
			details: this._getContentData(data),
			route: this._getRouteObject(head(legs)),
			encodedPolyline
		};
	}

	_getAgenciesNamesFromRoute({ legs }) {
		let steps = head(legs).steps;
		let agenciesNames = [];
		steps.map(step => {
			if (step.travel_mode === 'TRANSIT') {
				agenciesNames.push(this._getAgencyName(step.transit));
			}
		});
		return agenciesNames;
	}

	_getAgencyName({ line: { agencies } }) {
		return head(agencies).name;
	}

	_getRouteObject({
		arrival_time: arrivalTime,
		start_address: startAddress,
		end_address: endAddress,
		departure_time: departureTime,
		distance,
		duration,
		steps }) {
		return {
			arrivalTime,
			departureTime,
			startAddress,
			endAddress,
			distance,
			duration,
			steps: this._getStepsObject(steps)
		};
	}

	_getStepsObject(steps) {
		let obj;
		return steps.map(step => {
			obj = {
				startLocation: this._getLatlngsObject(step.start_location),
				endLocation: this._getLatlngsObject(step.end_location),
				distance: step.distance.text,
				duration: step.duration,
				instructions: step.instructions,
				travelMode: step.travel_mode,
				latlngs: this._getStepsLatlngs(step.lat_lngs)
			};

			if (step.travel_mode === 'TRANSIT') {
				obj.transitDetails = {
					name: this._getAgencyName(step.transit),
					arrivalStop: step.transit.arrival_stop,
					departureStop: step.transit.departure_stop,
					shortName: step.transit.line.short_name,
					arrivalTime: step.transit.arrival_time.value,
					departureTime: step.transit.departure_time.value
				};
			}

			return obj;
		});
	}

	_getLatlngsObject(latlng) {
		return {
			lat: latlng.lat(),
			lng: latlng.lng()
		};
	}

	_getStepsLatlngs(latlng) {
		return latlng.map(data => ({ lat: data.lat(), lng: data.lng() }));
	}

	_getLatLngObject({ lat, lng }) {
		return {
			lat: parseFloat(lat),
			lng: parseFloat(lng)
		}
	}


	_getContentData(data) {
		return data.map((value, index) => ({
			id: value.content_id,
			serviceArea: this._getServiceAreaObject(value.service_area),
			daysOfOperation: value.days_of_operation,
			email: value.email,
			hoursOfOperation: this._getHoursOfOperationDetails(value.hours_of_operation),
			image: value.image,
			name: value.name,
			fees: value.fees,
			notes: value.notes,
			methodOfPayment: this._getPaymentMethodObject(value.payment_methods),
			phone: this._getPhoneDetailsObject(value.phone_number),
			serviceName: value.service_area,
			serviceType: this._getServicetypeObject(value.service_type),
			url: value.url,
			costing: value.costing ? this._getCostingObject(value.costing) : { cost: [], value: 0 },
			isSelected: false,
			veteranSupport: value.veteran_support || false,
			medicaidSupport: value.medicaid_support || false
		}));
	}

	_getCostingObject(costing) {
		let costingObject = {
			value: costing,
			cost: []
		};

		times(3, value => {
			let obj = {};
			obj.status = costing !== 0 ? 'Active' : 'Deactive';
			obj.text = '$';
			costingObject.cost.unshift(obj);
			costing = costing !== 0 ? costing -= 1 : 0;
		});

		return costingObject;
	}

	_getHoursOfOperationDetails(value) {
		return {
			startTime: this.dateService.parseDateFromServerDate(value.start_time),
			endTime: this.dateService.parseDateFromServerDate(value.end_time),
			isAllDay: value.is_all_day,
			byAppointment: value.by_appoinment
		}
	}

	_getServiceAreaObject(values) {
		return values.map(value => ({
			id: value.service_area_id,
			name: value.name
		}));
	}

	_getServicetypeObject(values) {
		return values.map(value => ({
			id: value.service_type_id,
			name: value.service_type
		}));
	}

	_getPaymentMethodObject(values) {
		return values.map(value => ({
			id: value.payment_method_id,
			name: value.payment_method
		}));
	}

	_getPhoneDetailsObject(values) {
		return values.map(({ extn = '', number = '', name = '' }) => ({ extn, number, name }));
	}

	_getContentObject(values) {
		let details = filter(values, ['name', 'Providers']);
		let { privateProviders, paratransitProviders, volunteerProviders } = head(details).values;

		privateProviders.filtered = this._getContentData(privateProviders.filtered);
		privateProviders.others = this._getContentData(privateProviders.others);

		paratransitProviders.filtered = this._getContentData(paratransitProviders.filtered);
		paratransitProviders.others = this._getContentData(paratransitProviders.others);

		volunteerProviders.filtered = this._getContentData(volunteerProviders.filtered);
		volunteerProviders.others = this._getContentData(volunteerProviders.others);

		return {
			privateProviders,
			paratransitProviders,
			volunteerProviders
		};
	}

	_getServiceTypeObject(values) {
		let details = filter(values, ['name', 'serviceType']);
		return _getServiceTypeData(head(details).values);

		function _getServiceTypeData(data) {
			return data.map(value => ({
				id: value.service_type_id,
				name: value.name
			}));
		}
	}

	getLatlngDetailsObject(details) {
		return details.map(({ placeName, placeShortName, results }) => {
			return {
				"placeName": placeName,
				"placeShortName": this._removeCountyWordServiceArea(placeShortName),
				"stateCode": this._getStateCode(head(results)),
				"latlng": this._getLatLngObjectFromLocationDetails(head(results))
			}
		});
	}

	_getStateCode(placeDetails) {
		const { stateCode } = this.googleLocationService.getLocationObject(placeDetails);
		return stateCode;
	}

	_removeCountyWordServiceArea(ServiceArea) {
		return ServiceArea ? ServiceArea.replace('County', '').trim() : ServiceArea;
	}

	_getLatLngObjectFromLocationDetails({ geometry: { location: { lat, lng } } }) {
		return {
			"lat": lat(),
			"lng": lng()
		}
	}

	_setDirectionDetailsObject({
		originServiceAreas: origin_service_area,
		destinationServiceAreas: destination_service_area,
		isVeteran: veteran_support,
		isMedicaid: medicaid_support,
		requirements: special_requirement,
		serviceTypeName: service_type_name,
		serviceTypeId: service_type_id,
		departAt: depart_at }) {
		veteran_support = JSON.parse(veteran_support);
		medicaid_support = JSON.parse(medicaid_support);

		let providersObject = {
			origin_service_area: this._setServiceAreaObject(JSON.parse(origin_service_area)),
			destination_service_area: this._setServiceAreaObject(JSON.parse(destination_service_area)),
			veteran_support,
			medicaid_support,
			depart_at: !depart_at ? this.dateService.resetDateTimeWithOtherTime() : depart_at
		};

		providersObject = special_requirement ? assign(providersObject, { special_requirement }) : providersObject;
		providersObject = service_type_name ? assign(providersObject, { service_type_name }) : providersObject;
		providersObject = service_type_id ? assign(providersObject, { service_type_id }) : providersObject;

		return providersObject;
	}

	_setServiceAreaObject(values) {
		return values.map(({ placeShortName: name, latlng, stateCode }) => ({ name: this._removeCountyWordServiceArea(name), latlng, stateCode }));
	}
}


// WEBPACK FOOTER //
// ./src/app/components/trip-planner/trip-planner.service.js